"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "@/lib/aicheck/api-base";
import type { EvidenceLabelResolver } from "@/lib/aicheck/evidence-labeler";
import {
  buildEvidencePathOffsetMap,
  parseEvidenceArray,
} from "@/lib/aicheck/evidence-traceability";
import {
  ACTION_STATUS_LABELS,
  type ActionStatus,
} from "@/lib/aicheck/history/types";
import { hasProjectDataReference } from "@/lib/aicheck/project-data-reference";
import { authService } from "@/services/auth";
import {
  type FindingDecisionInput,
  useActionDecisionsStore,
} from "@/stores/actionDecisions";
import { useExpertFeedbackStore } from "@/stores/expertFeedback";
import { EvidenceList, ProjectDataReferenceButton } from "./EvidenceList";
import { MarkdownText } from "./MarkdownText";

interface FindingItem {
  severity?: string;
  finding?: string;
  evidence?: unknown;
  recommendation?: string;
  aspectId?: string;
  usedFactIds?: string[];
  [key: string]: unknown;
}

export interface FeedbackContext {
  runId?: string;
  projectId?: string;
  /** チェックID。指摘へのインライン採否（対応する/不要）に必要 */
  checkId?: string;
}

interface RenderContext {
  evidenceIdPrefix?: string;
  evidencePathOffsets: Map<string, number>;
  highlightedEvidenceIndex?: number | null;
  labelResolver?: EvidenceLabelResolver;
  onProjectDataReferenceClick?: (source: string) => void;
}

const DEFAULT_RENDER_CONTEXT: RenderContext = {
  evidencePathOffsets: new Map<string, number>(),
  highlightedEvidenceIndex: null,
};

function isFindingsArray(items: unknown[]): items is FindingItem[] {
  return items.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      ("finding" in item || "severity" in item),
  );
}

export function EvidenceValue({
  value,
  path,
  context,
}: {
  value: unknown;
  path?: string;
  context?: RenderContext;
}) {
  const resolvedContext = context ?? DEFAULT_RENDER_CONTEXT;
  const evidenceArr = parseEvidenceArray(value);
  if (evidenceArr && evidenceArr.length > 0) {
    return (
      <EvidenceList
        evidence={evidenceArr}
        showHeader={false}
        itemIdPrefix={resolvedContext.evidenceIdPrefix}
        itemIndexOffset={
          path ? (resolvedContext.evidencePathOffsets.get(path) ?? 0) : 0
        }
        highlightedEvidenceIndex={resolvedContext.highlightedEvidenceIndex}
        labelResolver={resolvedContext.labelResolver}
        onProjectDataReferenceClick={
          resolvedContext.onProjectDataReferenceClick
        }
      />
    );
  }

  if (typeof value === "string") {
    return (
      <div className="pl-2 border-l-2 border-gray-300">
        <EvidenceTextValue value={value} context={resolvedContext} />
      </div>
    );
  }

  return <ResultValue value={value} path={path} context={resolvedContext} />;
}

function EvidenceTextValue({
  value,
  context,
}: {
  value: string;
  context: RenderContext;
}) {
  const formattedValue = context.labelResolver
    ? context.labelResolver.formatEvidenceText(value)
    : value;
  const onProjectDataReferenceClick = context.onProjectDataReferenceClick;

  if (!onProjectDataReferenceClick) {
    return <MarkdownText mode="block">{formattedValue}</MarkdownText>;
  }

  const rawLines = value.split("\n");
  const formattedLines = formattedValue.split("\n");
  const lineCount = Math.max(rawLines.length, formattedLines.length);

  return (
    <div className="space-y-1">
      {Array.from({ length: lineCount }, (_, index) => {
        const rawLine = rawLines[index] ?? formattedLines[index] ?? "";
        const formattedLine = formattedLines[index] ?? rawLine;
        const canOpenProjectData = hasProjectDataReference(rawLine);

        return (
          <div key={`${index}:${rawLine}`} className="group flex gap-2">
            <div className="min-w-0 flex-1">
              <MarkdownText mode="block">{formattedLine}</MarkdownText>
            </div>
            {canOpenProjectData && (
              <ProjectDataReferenceButton
                source={rawLine}
                onOpen={onProjectDataReferenceClick}
                className="mt-0.5 h-5 opacity-90"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Feedback buttons (expert-nuance-check findings only) ────────────────────

/**
 * Hover tooltip explaining what a feedback/decision button records.
 * Rendered into document.body with fixed positioning so it is never clipped
 * by scroll containers (detail panels) or covered by the fixed nav frame.
 */
function ButtonTooltip({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover 専用のツールチップラッパー（操作は子のボタンが担う）
    <span
      className="inline-flex"
      onMouseEnter={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const halfWidth = 128; // w-64 / 2
        const x = Math.min(
          Math.max(rect.left + rect.width / 2, halfWidth + 8),
          window.innerWidth - halfWidth - 8,
        );
        setPos({ x, y: rect.top - 6 });
      }}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{ left: pos.x, top: pos.y }}
            className="pointer-events-none fixed z-[200] w-64 -translate-x-1/2 -translate-y-full rounded-md bg-zinc-800 px-2.5 py-1.5 text-[11px] font-normal leading-relaxed text-white shadow-lg"
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

type FeedbackVerdict = "useful" | "not_useful" | "outdated_info";

export function FeedbackButtons({
  aspectId,
  usedFactIds,
  feedbackCtx,
}: {
  aspectId: string;
  usedFactIds: string[];
  feedbackCtx: FeedbackContext;
}) {
  const { isSubmitted, markSubmitted } = useExpertFeedbackStore();
  const [localSubmitted, setLocalSubmitted] = useState<FeedbackVerdict | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const alreadySubmitted =
    localSubmitted !== null ||
    (!!feedbackCtx.projectId && isSubmitted(feedbackCtx.projectId, aspectId));

  async function send(verdict: FeedbackVerdict) {
    if (busy || alreadySubmitted) return;
    setBusy(true);
    setFailed(false);
    try {
      const token = await authService.getIdToken();
      if (!token) throw new Error("未認証です。再度サインインしてください。");
      const response = await fetch(
        apiUrl("/api/aicheck/expert-knowledge/feedback"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            aspectId,
            verdict,
            factIds: verdict === "outdated_info" ? usedFactIds : [],
            runId: feedbackCtx.runId,
            projectId: feedbackCtx.projectId,
          }),
        },
      );
      if (!response.ok) {
        setFailed(true);
        return;
      }
      setLocalSubmitted(verdict);
      if (feedbackCtx.projectId) {
        markSubmitted(feedbackCtx.projectId, aspectId);
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (alreadySubmitted) {
    return (
      <span className="text-xs text-gray-400 italic">フィードバック送信済</span>
    );
  }

  return (
    <div className="flex gap-1 mt-1.5">
      <ButtonTooltip text="この指摘は有用だったと記録します。指摘の元になった専門知識（観点）の評価が上がり、今後も同様の案件で指摘されやすくなります。">
        <button
          type="button"
          disabled={busy}
          onClick={() => send("useful")}
          className="text-xs px-1.5 py-0.5 rounded border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          👍有用
        </button>
      </ButtonTooltip>
      <ButtonTooltip text="この指摘は不要だったと記録します。観点の評価が下がり、低評価が続くと自動で退役候補になります。">
        <button
          type="button"
          disabled={busy}
          onClick={() => send("not_useful")}
          className="text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          👎不要
        </button>
      </ButtonTooltip>
      <ButtonTooltip text="この指摘が参照している事実情報（施設の状況・予約困難性など）が古い、または変わっていると報告します。該当の事実が再確認対象としてマークされます。">
        <button
          type="button"
          disabled={busy}
          onClick={() => send("outdated_info")}
          className="text-xs px-1.5 py-0.5 rounded border border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
        >
          情報が古い
        </button>
      </ButtonTooltip>
      {failed && (
        <span className="text-xs text-red-500 self-center">
          送信失敗（もう一度お試しください）
        </span>
      )}
    </div>
  );
}

// ─── Inline action decision (対応する / 不要) ─────────────────────────────────
//
// The natural feedback path: operators read a finding and record their
// judgement in place. The decision resolves (or lazily creates) the finding's
// suggested action server-side and feeds weighted feedback to expert
// knowledge — no separate action triage list required.

// 台帳（提案アクション）と同じ語彙で表示する。採用系のみ ✓ を付けて既決を示す。
function decidedLabel(status: ActionStatus): string {
  const label = ACTION_STATUS_LABELS[status] ?? status;
  return status === "adopted" || status === "adopted_modified"
    ? `✓ ${label}`
    : label;
}

export function ActionDecisionButtons({
  finding,
  feedbackCtx,
}: {
  finding: Record<string, unknown>;
  feedbackCtx: FeedbackContext;
}) {
  const { getStatus, decide, loadForProject } = useActionDecisionsStore();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const projectId = feedbackCtx.projectId ?? "";
  const checkId = feedbackCtx.checkId ?? "";

  useEffect(() => {
    if (projectId) void loadForProject(projectId);
  }, [projectId, loadForProject]);

  if (!projectId || !checkId) return null;

  const input: FindingDecisionInput = {
    source: "ai",
    checkId,
    checkPoint:
      typeof finding.check_point === "string" ? finding.check_point : "",
    dayNumber:
      typeof finding.day_number === "number" ? finding.day_number : null,
    severity: typeof finding.severity === "string" ? finding.severity : "info",
    finding: typeof finding.finding === "string" ? finding.finding : "",
    recommendation:
      typeof finding.recommendation === "string"
        ? finding.recommendation
        : null,
    aspectIds:
      typeof finding.aspectId === "string"
        ? finding.aspectId
            .split("/")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    factIds: Array.isArray(finding.usedFactIds)
      ? (finding.usedFactIds as string[]).filter((id) => id.startsWith("fact_"))
      : [],
  };

  const status = getStatus(projectId, input);
  if (status && status !== "open" && status !== "expired") {
    return (
      <span className="mt-1.5 inline-block text-xs italic text-gray-400">
        {decidedLabel(status)}
      </span>
    );
  }

  async function send(
    decision: "adopted" | "adopted_modified" | "dismissed",
    dismissReason?: string | null,
  ) {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const ok = await decide(projectId, input, decision, dismissReason);
    if (!ok) setFailed(true);
    setBusy(false);
  }

  return (
    <div className="flex gap-1 mt-1.5">
      <ButtonTooltip text="この指摘に対応する（対応した）と記録します（＝採用）。案件のアクション台帳に残り、指摘の元になった知識の評価が上がって今後の指摘精度に反映されます。">
        <button
          type="button"
          disabled={busy}
          onClick={() => void send("adopted")}
          className="text-xs px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
        >
          採用
        </button>
      </ButtonTooltip>
      <ButtonTooltip text="指摘を一部修正したうえで対応する（対応した）と記録します。採用と同じく知識の評価が上がります。">
        <button
          type="button"
          disabled={busy}
          onClick={() => void send("adopted_modified")}
          className="text-xs px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
        >
          修正して採用
        </button>
      </ButtonTooltip>
      <ButtonTooltip text="この指摘は対応不要と判断して棄却します。理由を添えると知識の改善材料になり、指摘の元になった知識の評価が少し下がります。">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            const reason = window.prompt(
              "棄却の理由（任意・知識の改善に使われます）",
              "",
            );
            if (reason === null) return;
            void send("dismissed", reason || null);
          }}
          className="text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          棄却
        </button>
      </ButtonTooltip>
      {failed && (
        <span className="text-xs text-red-500 self-center">
          記録失敗（もう一度お試しください）
        </span>
      )}
    </div>
  );
}

function FindingsSection({
  items,
  basePath,
  context,
  feedbackContext,
}: {
  items: FindingItem[];
  basePath: string;
  context: RenderContext;
  feedbackContext?: FeedbackContext;
}) {
  return (
    <div className="space-y-2">
      {items.map((f, i) => {
        const severityColor =
          f.severity === "critical"
            ? "border-red-200 bg-red-50/50"
            : f.severity === "warning"
              ? "border-yellow-200 bg-yellow-50/50"
              : "border-gray-200 bg-gray-50/50";
        const badgeColor =
          f.severity === "critical"
            ? "bg-red-100 text-red-700"
            : f.severity === "warning"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-gray-100 text-gray-600";

        const standardKeys = new Set([
          "severity",
          "finding",
          "evidence",
          "recommendation",
          "source_check_ids",
          "summary_sections",
          "evidence_refs",
          // Phase 2: internal tracking fields — not rendered as data rows
          "aspectId",
          "usedFactIds",
        ]);
        const extraEntries = Object.entries(f).filter(
          ([k, v]) =>
            !standardKeys.has(k) && v !== null && v !== undefined && v !== "",
        );

        // Feedback context for expert-nuance findings
        const hasAspect =
          typeof f.aspectId === "string" && f.aspectId.length > 0;
        const factIds = Array.isArray(f.usedFactIds)
          ? (f.usedFactIds as string[])
          : [];

        return (
          <div
            key={`finding-${i}`}
            className={`rounded-md border px-3 py-2 text-sm ${severityColor}`}
          >
            {f.severity && (
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-xs font-medium px-1.5 py-0.5 rounded ${badgeColor}`}
                >
                  {f.severity}
                </span>
              </div>
            )}
            {f.finding && (
              <div className="text-gray-700 mb-1">
                <MarkdownText mode="block">{f.finding}</MarkdownText>
              </div>
            )}
            {f.evidence != null && f.evidence !== "" && (
              <div className="my-1.5">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                  Evidence
                </div>
                <div className="text-xs text-gray-500 break-words">
                  <EvidenceValue
                    value={f.evidence}
                    path={`${basePath}[${i}].evidence`}
                    context={context}
                  />
                </div>
              </div>
            )}
            {f.recommendation && (
              <p className="text-xs text-gray-500 mt-1">
                <MarkdownText mode="inline">{f.recommendation}</MarkdownText>
              </p>
            )}
            {extraEntries.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {extraEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-2 min-w-0 text-xs">
                    <span className="font-medium text-gray-400 shrink-0">
                      {snakeToTitle(k)}:
                    </span>
                    <div className="min-w-0 break-words">
                      <ResultValue
                        value={v}
                        fieldKey={k}
                        path={`${basePath}[${i}].${k}`}
                        context={context}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              {hasAspect && feedbackContext && (
                <FeedbackButtons
                  aspectId={f.aspectId as string}
                  usedFactIds={factIds}
                  feedbackCtx={feedbackContext}
                />
              )}
              {feedbackContext?.projectId && feedbackContext.checkId && (
                <ActionDecisionButtons
                  finding={f}
                  feedbackCtx={feedbackContext}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function snakeToTitle(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function ResultValue({
  value,
  fieldKey,
  path,
  context,
}: {
  value: unknown;
  fieldKey?: string;
  path?: string;
  context?: RenderContext;
}) {
  const resolvedContext = context ?? DEFAULT_RENDER_CONTEXT;
  const evidenceArr = parseEvidenceArray(value);
  if (evidenceArr && evidenceArr.length > 0) {
    return (
      <EvidenceList
        evidence={evidenceArr}
        itemIdPrefix={resolvedContext.evidenceIdPrefix}
        itemIndexOffset={
          path ? (resolvedContext.evidencePathOffsets.get(path) ?? 0) : 0
        }
        highlightedEvidenceIndex={resolvedContext.highlightedEvidenceIndex}
        labelResolver={resolvedContext.labelResolver}
        onProjectDataReferenceClick={
          resolvedContext.onProjectDataReferenceClick
        }
      />
    );
  }
  if (fieldKey?.toLowerCase() === "evidence") {
    return (
      <EvidenceValue value={value} path={path} context={resolvedContext} />
    );
  }

  if (value === null || value === undefined) return <span>—</span>;

  if (typeof value !== "object") {
    if (
      typeof value === "string" &&
      (fieldKey === "source_field" || fieldKey === "source")
    ) {
      const formatted = resolvedContext.labelResolver?.formatSource(value) ?? {
        display: value,
        raw: null,
      };
      const onProjectDataReferenceClick =
        resolvedContext.onProjectDataReferenceClick;
      const canOpenProjectData =
        onProjectDataReferenceClick && hasProjectDataReference(value);
      return (
        <div className="space-y-0.5">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 text-xs text-gray-800 break-words">
              {formatted.display}
            </div>
            {canOpenProjectData && (
              <ProjectDataReferenceButton
                source={value}
                onOpen={onProjectDataReferenceClick}
                className="h-5"
              />
            )}
          </div>
          {formatted.raw && (
            <div className="font-mono text-[11px] text-gray-400 break-all">
              {formatted.raw}
            </div>
          )}
        </div>
      );
    }

    return (
      <MarkdownText mode="block" className="text-xs text-gray-800">
        {String(value)}
      </MarkdownText>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-xs text-gray-400">—</span>;
    }

    return (
      <div className="space-y-1 mt-1">
        {value.map((sub, i) => (
          <div
            key={i}
            className="rounded border border-gray-100 bg-gray-50 p-2"
          >
            {typeof sub === "object" && sub !== null ? (
              <div className="space-y-1">
                {Object.entries(sub as Record<string, unknown>).map(
                  ([k, v]) => (
                    <div key={k} className="flex gap-2 min-w-0">
                      <span className="text-xs font-medium text-gray-500 shrink-0">
                        {snakeToTitle(k)}:
                      </span>
                      <div className="min-w-0 break-words">
                        <ResultValue
                          value={v}
                          fieldKey={k}
                          path={`${path ?? ""}[${i}].${k}`}
                          context={resolvedContext}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-800">{String(sub)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1 min-w-0">
      {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
        <div key={k} className="flex gap-2 min-w-0">
          <span className="text-xs font-medium text-gray-500 shrink-0">
            {snakeToTitle(k)}:
          </span>
          <div className="min-w-0 break-words">
            <ResultValue
              value={v}
              fieldKey={k}
              path={path ? `${path}.${k}` : k}
              context={resolvedContext}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function StructuredResultView({
  result,
  excludeKeys,
  evidenceIdPrefix,
  highlightedEvidenceIndex,
  labelResolver,
  onProjectDataReferenceClick,
  feedbackContext,
}: {
  result: Record<string, unknown>;
  excludeKeys?: string[];
  evidenceIdPrefix?: string;
  highlightedEvidenceIndex?: number | null;
  labelResolver?: EvidenceLabelResolver;
  onProjectDataReferenceClick?: (source: string) => void;
  feedbackContext?: FeedbackContext;
}) {
  const excluded = excludeKeys ? new Set(excludeKeys) : null;
  const context = useMemo<RenderContext>(
    () => ({
      evidenceIdPrefix,
      evidencePathOffsets: buildEvidencePathOffsetMap(result),
      highlightedEvidenceIndex,
      labelResolver,
      onProjectDataReferenceClick,
    }),
    [
      evidenceIdPrefix,
      highlightedEvidenceIndex,
      labelResolver,
      onProjectDataReferenceClick,
      result,
    ],
  );
  const arraySections: { key: string; items: unknown[] }[] = [];
  const objectSections: { key: string; value: Record<string, unknown> }[] = [];
  const scalarFields: { key: string; value: unknown }[] = [];

  for (const [key, value] of Object.entries(result)) {
    if (excluded?.has(key)) continue;
    if (Array.isArray(value) && value.length > 0) {
      arraySections.push({ key, items: value });
    } else if (
      value !== null &&
      value !== undefined &&
      typeof value === "object"
    ) {
      objectSections.push({ key, value: value as Record<string, unknown> });
    } else if (value !== null && value !== undefined) {
      scalarFields.push({ key, value });
    }
  }

  return (
    <div className="space-y-4">
      {scalarFields.map(({ key, value }) => (
        <div key={key}>
          <div className="text-xs font-medium text-gray-500 mb-1">
            {snakeToTitle(key)}
          </div>
          <div className="text-sm text-gray-800">
            <ResultValue
              value={value}
              fieldKey={key}
              path={key}
              context={context}
            />
          </div>
        </div>
      ))}
      {objectSections.map(({ key, value }) => (
        <div key={key}>
          <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
            {snakeToTitle(key)}
          </div>
          <div className="rounded-md border border-gray-200 bg-white p-3 text-sm overflow-hidden">
            <div className="space-y-1">
              {Object.entries(value).map(([k, v]) => (
                <div key={k} className="flex gap-2 min-w-0">
                  <span className="text-xs font-medium text-gray-500 shrink-0">
                    {snakeToTitle(k)}:
                  </span>
                  <div className="min-w-0 break-words">
                    <ResultValue
                      value={v}
                      fieldKey={k}
                      path={`${key}.${k}`}
                      context={context}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      {arraySections.map(({ key, items }) => (
        <div key={key}>
          <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
            {snakeToTitle(key)} ({items.length})
          </div>
          {key === "findings" && isFindingsArray(items) ? (
            <FindingsSection
              items={items}
              basePath={key}
              context={context}
              feedbackContext={feedbackContext}
            />
          ) : (
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-gray-200 bg-white p-3 text-sm overflow-hidden"
                >
                  {typeof item === "object" && item !== null ? (
                    <div className="space-y-1">
                      {Object.entries(item as Record<string, unknown>).map(
                        ([k, v]) => (
                          <div key={k} className="flex gap-2 min-w-0">
                            <span className="text-xs font-medium text-gray-500 shrink-0">
                              {snakeToTitle(k)}:
                            </span>
                            <div className="min-w-0 break-words">
                              <ResultValue
                                value={v}
                                fieldKey={k}
                                path={`${key}[${idx}].${k}`}
                                context={context}
                              />
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-700">{String(item)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-800 mb-2">
          Show raw JSON
        </summary>
        <pre className="max-h-64 overflow-auto rounded-md bg-white p-3 text-xs text-gray-700 border border-gray-200">
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}
